import { apiFetch } from '../config/api';
import {
  isActiveCarpoolLine,
  resolveStudentCarpoolLine,
  sortCarpoolLinesForFallback,
} from '@car-pool/shared/carpoolLines';

export const DEFAULT_SIMULATION_SPEED = 1500;
const DEFAULT_MAX_PICKUP_CONTACTS = 30;
const DEFAULT_MAX_STUDENTS = Number.POSITIVE_INFINITY;
const simulationControllers = new Map();

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function parseChildrenIds(value) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.map(Number).filter((item) => !Number.isNaN(item));
  }

  if (typeof value === 'string') {
    try {
      return parseChildrenIds(JSON.parse(value));
    } catch {
      return value
        .split(',')
        .map((item) => Number(item.trim()))
        .filter((item) => !Number.isNaN(item));
    }
  }

  return [];
}

function isPickupContact(contact) {
  return ['parent', 'guardian', 'driver'].includes(String(contact?.type || '').toLowerCase());
}

function getStudentLinkedContactIds(student) {
  return parseChildrenIds(
    student?.guardians_permanent_authorized_driver
    || student?.['guardians/permanent_authorized_driver']
    || []
  );
}

function getContactPriority(contact) {
  switch (String(contact?.type || '').toLowerCase()) {
    case 'parent':
      return 0;
    case 'guardian':
      return 1;
    case 'driver':
      return 2;
    default:
      return 3;
  }
}

function getContactName(contact) {
  return [contact?.f_name, contact?.l_name].filter(Boolean).join(' ').trim() || 'Unlinked Contact';
}

function buildPickupContactByStudentId({ students = [], contacts = [] } = {}) {
  const pickupContacts = contacts.filter(isPickupContact);
  const contactsById = Object.fromEntries(pickupContacts.map((contact) => [Number(contact.id), contact]));
  const linkedContactsByStudentId = new Map();

  const link = (studentId, contact) => {
    const normalizedStudentId = Number(studentId);
    const normalizedContactId = Number(contact?.id);
    if (!normalizedStudentId || !normalizedContactId) return;

    if (!linkedContactsByStudentId.has(normalizedStudentId)) {
      linkedContactsByStudentId.set(normalizedStudentId, new Map());
    }
    linkedContactsByStudentId.get(normalizedStudentId).set(normalizedContactId, contact);
  };

  pickupContacts.forEach((contact) => {
    parseChildrenIds(contact.children_ids).forEach((studentId) => {
      link(studentId, contact);
    });
  });

  students.forEach((student) => {
    getStudentLinkedContactIds(student).forEach((contactId) => {
      const contact = contactsById[Number(contactId)];
      if (contact) {
        link(student.id, contact);
      }
    });
  });

  return Object.fromEntries(
    [...linkedContactsByStudentId.entries()].map(([studentId, contactMap]) => {
      const linkedContacts = [...contactMap.values()].sort((left, right) => {
        const priorityDelta = getContactPriority(left) - getContactPriority(right);
        if (priorityDelta !== 0) return priorityDelta;
        return Number(left.id) - Number(right.id);
      });

      return [studentId, linkedContacts[0] || null];
    })
  );
}

export function getStudentIdsByParentId(simState = {}) {
  const studentIdsByParentId = new Map();

  Object.entries(simState).forEach(([studentId, entry]) => {
    const parentId = Number(entry?.parentId);
    if (!parentId) return;

    if (!studentIdsByParentId.has(parentId)) {
      studentIdsByParentId.set(parentId, []);
    }

    studentIdsByParentId.get(parentId).push(Number(studentId));
  });

  return studentIdsByParentId;
}

function getCurrentSchoolController(schoolId) {
  return simulationControllers.get(Number(schoolId));
}

function getActiveDriversInZone(events = []) {
  const latestByScope = new Map();

  events.forEach((event) => {
    const driverId = Number(event?.parent_driver_id);
    const studentId = Number(event?.student_id);
    const scopeKey = driverId
      ? `contact:${driverId}`
      : (studentId ? `student:${studentId}` : null);
    if (!scopeKey) return;

    const previous = latestByScope.get(scopeKey);
    const eventTime = new Date(event.timestamp).getTime();
    const previousTime = previous ? new Date(previous.timestamp).getTime() : 0;
    const eventId = Number(event?.id) || 0;
    const previousId = Number(previous?.id) || 0;

    if (!previous || eventTime > previousTime || (eventTime === previousTime && eventId > previousId)) {
      latestByScope.set(scopeKey, event);
    }
  });

  return [...latestByScope.values()].filter((event) => event?.action === 'enter');
}

export async function loadDismissalSimulationSeed({
  schoolId,
  token,
  maxStudents = DEFAULT_MAX_STUDENTS,
  maxPickupContacts = DEFAULT_MAX_PICKUP_CONTACTS,
}) {
  const normalizedSchoolId = Number(schoolId);
  if (!normalizedSchoolId) {
    return { simState: {}, carpoolLines: [], studentCount: 0, pickupContactCount: 0, skippedStudentCount: 0 };
  }

  const [schoolRes, studentsRes, contactsRes, campusesRes, linesRes, assignmentsRes] = await Promise.all([
    apiFetch(`/schools/${normalizedSchoolId}`, {}, token).catch(() => ({ data: null })),
    apiFetch(`/students?school_id=${normalizedSchoolId}&status=active`, {}, token),
    apiFetch(`/contacts?school_id=${normalizedSchoolId}`, {}, token),
    apiFetch(`/campuses/school/${normalizedSchoolId}`, {}, token),
    apiFetch('/carpool-lines?limit=50', {}, token),
    apiFetch('/student-line-assigned/active', {}, token),
  ]);

  const allStudents = studentsRes.data || [];
  const campusIds = new Set((campusesRes.data || []).map((campus) => Number(campus.campus_id)));
  const carpoolLines = sortCarpoolLinesForFallback(
    (linesRes.data || [])
      .filter((line) => campusIds.has(Number(line.campus_id)))
      .filter(isActiveCarpoolLine)
  );
  const assignmentByStudentId = new Map(
    (assignmentsRes.data || []).map((assignment) => [Number(assignment.student_id), assignment])
  );
  const pickupContactByStudentId = buildPickupContactByStudentId({
    students: allStudents,
    contacts: contactsRes.data || [],
  });
  const selectedStudents = [];
  const selectedContactIds = new Set();
  const seenContactIds = new Set();

  allStudents.forEach((student) => {
    const pickupContact = pickupContactByStudentId[Number(student.id)];
    const pickupContactId = Number(pickupContact?.id);
    if (!pickupContactId || seenContactIds.has(pickupContactId)) return;
    if (selectedContactIds.size >= maxPickupContacts) return;

    const contactStudents = allStudents.filter((candidate) => Number(pickupContactByStudentId[Number(candidate.id)]?.id) === pickupContactId);
    if (!contactStudents.length) return;

    if (selectedStudents.length > 0 && selectedStudents.length + contactStudents.length > maxStudents) {
      return;
    }

    seenContactIds.add(pickupContactId);
    selectedContactIds.add(pickupContactId);
    selectedStudents.push(...contactStudents);
  });

  const simState = {};
  selectedStudents.forEach((student) => {
    const parent = pickupContactByStudentId[Number(student.id)];
    const { line } = resolveStudentCarpoolLine({
      student,
      explicitAssignment: assignmentByStudentId.get(Number(student.id)) || null,
      lines: carpoolLines,
      groupingMode: schoolRes.data?.student_grouping_mode,
    });
    simState[student.id] = {
      status: 'waiting',
      parentId: parent?.id || null,
      parentName: parent ? getContactName(parent) : 'Unlinked Contact',
      studentName: `${student.f_name} ${student.l_name}`,
      grade: student.grade,
      carpoolLineId: line?.carpool_line_id ? Number(line.carpool_line_id) : null,
    };
  });

  return {
    simState,
    carpoolLines,
    studentCount: selectedStudents.length,
    pickupContactCount: selectedContactIds.size,
    skippedStudentCount: Math.max(0, allStudents.length - selectedStudents.length),
  };
}

async function postGeofenceEvent({ schoolId, token, parentId, carpoolLineId, action }) {
  if (!parentId) return;

  await apiFetch('/geofenced-data', {
    method: 'POST',
    body: JSON.stringify({
      parent_driver_id: parentId,
      school_id: Number(schoolId),
      action,
      timestamp: new Date().toISOString(),
      carpool_line_id: carpoolLineId,
    }),
  }, token);
}

async function postStudentGeofenceExit({ schoolId, token, studentId, carpoolLineId }) {
  if (!studentId) return;

  await apiFetch('/geofenced-data/manual-student-exit', {
    method: 'POST',
    body: JSON.stringify({
      school_id: Number(schoolId),
      student_id: Number(studentId),
      carpool_line_id: carpoolLineId || undefined,
    }),
  }, token);
}

async function updateDismissalStatus({ schoolId, token, studentId, status }) {
  await apiFetch('/dismissal-status', {
    method: 'PUT',
    body: JSON.stringify({
      student_id: studentId,
      school_id: Number(schoolId),
      status,
    }),
  }, token);
}

export function isDismissalSimulationRunning(schoolId) {
  return Boolean(getCurrentSchoolController(schoolId)?.running);
}

export async function abortDismissalSimulation({ schoolId } = {}) {
  const controller = getCurrentSchoolController(schoolId);
  if (!controller) return;

  controller.aborted = true;
  await controller.promise.catch(() => {});
}

export async function resetDismissalSimulation({ schoolId, token }) {
  const normalizedSchoolId = Number(schoolId);
  if (!normalizedSchoolId) return;

  await apiFetch(`/dismissal-status?school_id=${normalizedSchoolId}`, { method: 'DELETE' }, token);

  const geofenceRes = await apiFetch(`/geofenced-data/school/${normalizedSchoolId}`, {}, token).catch(() => ({ data: [] }));
  const activeDrivers = getActiveDriversInZone(geofenceRes.data || []);

  for (const driverEvent of activeDrivers) {
    if (driverEvent.parent_driver_id) {
      await postGeofenceEvent({
        schoolId: normalizedSchoolId,
        token,
        parentId: driverEvent.parent_driver_id,
        carpoolLineId: driverEvent.carpool_line_id ?? null,
        action: 'exit',
      });
    } else if (driverEvent.student_id) {
      await postStudentGeofenceExit({
        schoolId: normalizedSchoolId,
        token,
        studentId: driverEvent.student_id,
        carpoolLineId: driverEvent.carpool_line_id ?? null,
      });
    }
  }
}

export async function stopDismissalSimulation({ schoolId, token }) {
  await abortDismissalSimulation({ schoolId });
  await resetDismissalSimulation({ schoolId, token });
}

export function startDismissalSimulation({
  schoolId,
  token,
  speed = DEFAULT_SIMULATION_SPEED,
  simState = null,
  carpoolLines = null,
  onPhaseChange,
  onStatusChange,
  onLog,
} = {}) {
  const normalizedSchoolId = Number(schoolId);
  if (!normalizedSchoolId) {
    return Promise.reject(new Error('schoolId is required'));
  }

  const existing = getCurrentSchoolController(normalizedSchoolId);
  if (existing?.running) {
    return existing.promise;
  }

  const controller = {
    aborted: false,
    running: true,
    promise: null,
  };

  const emitStatus = (studentId, status, entry) => {
    onStatusChange?.(studentId, status, entry);
  };

  controller.promise = (async () => {
    try {
      const seed = simState && Object.keys(simState).length
        ? { simState, carpoolLines: carpoolLines || [] }
        : await loadDismissalSimulationSeed({ schoolId: normalizedSchoolId, token });

      const resolvedSimState = seed.simState || {};
      const resolvedCarpoolLines = seed.carpoolLines || [];
      const defaultCarpoolLineId = resolvedCarpoolLines[0]?.carpool_line_id || null;
      const ids = Object.keys(resolvedSimState).map(Number);
      const studentIdsByParentId = getStudentIdsByParentId(resolvedSimState);
      const getCarpoolLineIdForStudents = (studentIds = []) => (
        studentIds
          .map((studentId) => resolvedSimState[studentId]?.carpoolLineId)
          .map(Number)
          .find((lineId) => Number.isInteger(lineId) && lineId > 0)
        || defaultCarpoolLineId
        || null
      );

      if (!ids.length) return;

      const setLocalStatus = (studentId, status) => {
        const entry = resolvedSimState[studentId];
        if (!entry) return;
        entry.status = status;
        emitStatus(studentId, status, entry);
      };

      onPhaseChange?.('arriving');
      onLog?.('--- Phase 1: Parents arriving ---', 'phase');

      for (const [parentId, studentIds] of studentIdsByParentId.entries()) {
        if (controller.aborted) return;
        const entry = resolvedSimState[studentIds[0]];
        try {
          await postGeofenceEvent({
            schoolId: normalizedSchoolId,
            token,
            parentId,
            carpoolLineId: getCarpoolLineIdForStudents(studentIds),
            action: 'enter',
          });
          studentIds.forEach((studentId) => {
            setLocalStatus(studentId, 'arriving');
          });
          onLog?.(
            `🚗 ${entry.parentName} entered geofence (${studentIds.length === 1 ? `child: ${entry.studentName}` : `${studentIds.length} students`})`,
            'arrive'
          );
        } catch (error) {
          onLog?.(`Failed geofence enter for ${entry.parentName}: ${error.message}`, 'error');
        }
        await delay(speed * 0.3);
      }

      if (controller.aborted) return;

      onPhaseChange?.('calling');
      onLog?.('--- Phase 2: Calling students ---', 'phase');

      for (const studentId of ids) {
        if (controller.aborted) return;
        const entry = resolvedSimState[studentId];
        try {
          await updateDismissalStatus({
            schoolId: normalizedSchoolId,
            token,
            studentId,
            status: 'called',
          });
          setLocalStatus(studentId, 'called');
          onLog?.(`📢 ${entry.studentName} called to carpool`, 'call');
        } catch (error) {
          onLog?.(`Failed to call ${entry.studentName}: ${error.message}`, 'error');
        }
        await delay(speed * 0.4);
      }

      if (controller.aborted) return;

      onPhaseChange?.('sending');
      onLog?.('--- Phase 3: Sending to carpool ---', 'phase');

      for (const studentId of ids) {
        if (controller.aborted) return;
        const entry = resolvedSimState[studentId];
        try {
          await updateDismissalStatus({
            schoolId: normalizedSchoolId,
            token,
            studentId,
            status: 'sent',
          });
          setLocalStatus(studentId, 'sent');
          onLog?.(`✅ ${entry.studentName} sent to carpool`, 'send');
        } catch (error) {
          onLog?.(`Failed to send ${entry.studentName}: ${error.message}`, 'error');
        }

        await delay(speed * 0.2);
        if (controller.aborted) return;

        const siblingIds = studentIdsByParentId.get(Number(entry.parentId)) || [studentId];
        const hasPendingSiblings = siblingIds.some((siblingId) => (
          siblingId !== studentId
          && !['sent', 'pickedUp'].includes(resolvedSimState[siblingId]?.status)
        ));

        if (hasPendingSiblings) {
          await delay(speed * 0.3);
          continue;
        }

        const sentSiblingIds = siblingIds.filter((siblingId) => resolvedSimState[siblingId]?.status === 'sent');
        if (!sentSiblingIds.length) {
          await delay(speed * 0.3);
          continue;
        }

        try {
          for (const siblingId of sentSiblingIds) {
            // eslint-disable-next-line no-await-in-loop
            await updateDismissalStatus({
              schoolId: normalizedSchoolId,
              token,
              studentId: siblingId,
              status: 'picked_up',
            });
          }

          await postGeofenceEvent({
            schoolId: normalizedSchoolId,
            token,
            parentId: entry.parentId,
            carpoolLineId: getCarpoolLineIdForStudents(sentSiblingIds),
            action: 'exit',
          });
          sentSiblingIds.forEach((siblingId) => {
            setLocalStatus(siblingId, 'pickedUp');
          });
          onLog?.(
            `🏁 ${entry.parentName} left with ${sentSiblingIds.map((siblingId) => resolvedSimState[siblingId]?.studentName).filter(Boolean).join(', ')}`,
            'pickup'
          );
        } catch (error) {
          onLog?.(`Failed geofence exit for ${entry.parentName}: ${error.message}`, 'error');
        }

        await delay(speed * 0.3);
      }

      onPhaseChange?.('complete');
      onLog?.('--- Simulation complete ---', 'phase');
    } finally {
      controller.running = false;
      simulationControllers.delete(normalizedSchoolId);
    }
  })();

  simulationControllers.set(normalizedSchoolId, controller);
  return controller.promise;
}
